import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { MM_PER_OFFSET_COL, printOffsetMm } from '@/lib/printOffset'

/**
 * Alignment card for the HTML print path (F-FE-N156).
 *
 * The phone path has had a text alignment card since F-FE-N113
 * (`bhooklyAppPrint.ts`). This is its desktop twin: the same question — "how
 * much of the left edge is this printer losing?" — answered on paper with a
 * ruler the operator can count, in the same unit the nudge field takes.
 *
 * Read it on the paper:
 *   • The left ruler starts at the edge of the printable area. Cell 1 should be
 *     complete. Count the cells that are cut off or missing and enter that
 *     number as the nudge.
 *   • The right ruler ends at the printable area's right edge. If IT is cut
 *     after nudging, the roll is narrower than "Printable Area" says — lower it.
 *
 * Every cell is one step, 1.5 mm, exactly one ESC/POS column, so the number
 * read off a desktop card means the same distance as on a phone-printed slip.
 *
 * The card is a full document built on `thermalPrintBaseCss`, so it goes down
 * the same pipeline as a real slip (same @page, same frame) — and it is printed
 * WITH the current nudge, so reprinting after a change confirms the fix.
 */

export const ALIGNMENT_RULER_CELLS = 12

export type AlignmentCardOpts = {
  paperWidthMm: number
  printableAreaMm: number
  offsetCols: number
  /** Injectable for tests; defaults to now. */
  now?: Date
}

function ruler(side: 'left' | 'right'): string {
  const cells: string[] = []
  for (let i = 1; i <= ALIGNMENT_RULER_CELLS; i++) {
    const n = side === 'left' ? i : ALIGNMENT_RULER_CELLS + 1 - i
    cells.push(`<span class="cell${n % 2 === 0 ? ' cell--dark' : ''}">${n}</span>`)
  }
  return `<div class="ruler ruler--${side}">${cells.join('')}</div>`
}

export function buildAlignmentCardHtml(opts: AlignmentCardOpts): string {
  const paper = Math.max(40, Math.min(112, Math.round(opts.paperWidthMm) || 80))
  const printable = Math.max(40, Math.min(110, Math.round(opts.printableAreaMm) || 72))
  const cols = Math.max(0, Math.min(8, Math.round(opts.offsetCols) || 0))
  const nudgeMm = printOffsetMm(cols)
  const when = (opts.now ?? new Date()).toLocaleString()

  const baseCss = thermalPrintBaseCss({
    paperWidthMm: paper,
    printableAreaMm: printable,
    fontScale: 1,
  })

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Alignment card</title>
<style>
  ${baseCss}
  body {
    font-family: ui-monospace, 'Cascadia Mono', Consolas, monospace;
    font-size: 12px;
    line-height: 1.35;
  }
  .thermal-page { padding-left: 0; padding-right: 0; }
  h1 { font-size: 14px; text-align: center; margin: 0 0 2mm; letter-spacing: 0.5px; }
  .meta { text-align: center; margin: 0 0 3mm; font-size: 11px; }
  .bar { position: relative; height: 4mm; background: #000; margin: 1mm 0 1mm; }
  .bar-labels { display: flex; justify-content: space-between; font-weight: 700; font-size: 11px; }
  .ruler { display: flex; margin: 1mm 0; }
  .ruler--right { justify-content: flex-end; }
  .cell {
    display: inline-block;
    box-sizing: border-box;
    width: ${MM_PER_OFFSET_COL}mm;
    height: 6mm;
    border: 0.25mm solid #000;
    border-right-width: 0;
    font-size: 7px;
    line-height: 6mm;
    text-align: center;
    overflow: hidden;
  }
  .cell:last-child { border-right-width: 0.25mm; }
  .cell--dark { background: #000; color: #fff; }
  .how { margin: 3mm 0 0; font-size: 11px; }
  .how p { margin: 0 0 1.5mm; }
</style></head><body><div class="thermal-page">
  <h1>*** ALIGNMENT CARD ***</h1>
  <div class="meta">${when}<br/>paper ${paper} mm &middot; printable ${printable} mm &middot; nudge ${cols} (${nudgeMm} mm)</div>
  <div class="bar-labels"><span>L</span><span>FULL PRINTABLE WIDTH</span><span>R</span></div>
  <div class="bar"></div>
  ${ruler('left')}
  ${ruler('right')}
  <div class="how">
    <p>Cell 1 on the LEFT ruler should be complete. Count the cells that are cut off or missing and enter that number as the nudge.</p>
    <p>If the RIGHT ruler is cut after nudging, lower Printable Area.</p>
    <p>Each cell is one step (${MM_PER_OFFSET_COL} mm).</p>
  </div>
</div></body></html>`
}
